import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DealerSurveyData } from '@/api/types';
import { useTranslation } from 'react-i18next';

interface FormStepFourProps {
  formData: DealerSurveyData;
  updateFormData: (formData: Partial<DealerSurveyData>) => void;
  readOnly?: boolean;
}

const FormStepFour: React.FC<FormStepFourProps> = ({ formData, updateFormData, readOnly = false }) => {
  const { t } = useTranslation('common');

  const handleRadioChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  const handleCheckboxChange = (field: keyof DealerSurveyData, value: string, checked: boolean) => {
    const currentArray = formData[field] as string[] || [];
    let newArray: string[];
    
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray.filter(item => item !== value);
    }
    
    updateFormData({ [field]: newArray });
  };

  const handleTextChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  return (
    <div className={`space-y-8 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
      {/* Očekávaná podpora */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_4.expected_support_title')}
        </Label>
        <div className="space-y-3">
          {[
            { value: 'marketing', label: t('form_step_4.support_marketing') },
            { value: 'training', label: t('form_step_4.support_training') },
            { value: 'service', label: t('form_step_4.support_service') },
            { value: 'none', label: t('form_step_4.support_none') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`support-${option.value}`}
                checked={formData.expectedSupport?.includes(option.value) || false}
                onCheckedChange={(checked) => 
                  handleCheckboxChange('expectedSupport', option.value, checked as boolean)
                }
              />
              <Label htmlFor={`support-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="otherSupport" className="text-slate-700">
            {t('form_step_4.support_other')}
          </Label>
          <Input
            id="otherSupport"
            value={formData.otherSupport || ''}
            onChange={(e) => handleTextChange('otherSupport', e.target.value)}
            placeholder={t('form_step_4.support_other_placeholder')}
            className="border-slate-300 focus:border-amber-500"
          />
        </div>
      </div>

      {/* Způsob prodeje */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_4.sales_method_title')}
        </Label>
        <div className="space-y-3">
          {[
            { value: 'with-installation', label: t('form_step_4.sales_with_installation') },
            { value: 'without-installation', label: t('form_step_4.sales_without_installation') },
            { value: 'eshop', label: t('form_step_4.sales_eshop') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`sales-${option.value}`}
                checked={formData.salesMethod?.includes(option.value) || false}
                onCheckedChange={(checked) => 
                  handleCheckboxChange('salesMethod', option.value, checked as boolean)
                }
              />
              <Label htmlFor={`sales-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Dodací lhůta */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_4.delivery_time_title')}
        </Label>
        <RadioGroup
          value={formData.maxDeliveryTime}
          onValueChange={(value) => handleRadioChange('maxDeliveryTime', value)}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {[
            { value: 'in-stock', label: t('form_step_4.delivery_in_stock') },
            { value: 'one-month', label: t('form_step_4.delivery_one_month') },
            { value: 'two-months', label: t('form_step_4.delivery_two_months') },
            { value: 'three-months', label: t('form_step_4.delivery_three_months') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`delivery-${option.value}`} />
              <Label htmlFor={`delivery-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Prodejní potenciál */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2">
          {t('form_step_4.sales_potential_title')}
        </h3>
        
        <div className="space-y-2">
          <Label htmlFor="typicalCustomer" className="text-slate-700 font-medium">
            {t('form_step_4.typical_customer')}
          </Label>
          <Input
            id="typicalCustomer"
            value={formData.typicalCustomer || ''}
            onChange={(e) => handleTextChange('typicalCustomer', e.target.value)}
            placeholder={t('form_step_4.typical_customer_placeholder')}
            className="border-slate-300 focus:border-amber-500"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="totalStovesPerYear" className="text-slate-700 font-medium">
              {t('form_step_4.total_stoves_per_year')}
            </Label>
            <Input
              id="totalStovesPerYear"
              type="number"
              value={formData.totalStovesPerYear || ''}
              onChange={(e) => handleTextChange('totalStovesPerYear', e.target.value)}
              placeholder={t('form_step_4.total_stoves_placeholder')}
              className="border-slate-300 focus:border-amber-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="designStovesPerYear" className="text-slate-700 font-medium">
              {t('form_step_4.design_stoves_per_year')}
            </Label>
            <Input
              id="designStovesPerYear"
              type="number"
              value={formData.designStovesPerYear || ''}
              onChange={(e) => handleTextChange('designStovesPerYear', e.target.value)}
              placeholder={t('form_step_4.design_stoves_placeholder')}
              className="border-slate-300 focus:border-amber-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormStepFour; 